import { Link, useNavigate } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { useEffect, useRef, useState } from "react";
import {
  ArrowRight, FileText, Shield, Zap, BarChart3,
  IndianRupee, Users, Lock, CheckCircle2, TrendingUp,
  AlertTriangle, ChevronRight, ChevronDown, Layers, RefreshCw,
  ShieldCheck, Server, Eye, ArrowUpRight, Building2,
  Briefcase, Network, Send, UserCheck, Receipt
} from "lucide-react";
import SEO from "../seo/SEO";
import { useT } from "../seo/i18n";
import {
  ORGANIZATION_SCHEMA, SOFTWARE_SCHEMA, WEBSITE_SCHEMA, buildFAQSchema,
} from "../seo/seoConfig";

/* ── animation presets ── */
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.25, 0.46, 0.45, 0.94] } },
};
const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.5 } },
};
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };
const staggerFast = { visible: { transition: { staggerChildren: 0.06 } } };

/* ── reusable section wrapper with inView trigger ── */
function Section({ children, className = "", id, testId }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.section
      ref={ref}
      id={id}
      data-testid={testId}
      className={className}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      variants={stagger}
    >
      {children}
    </motion.section>
  );
}

/* ── font helpers ── */
const heading = { fontFamily: "'Satoshi', sans-serif" };
const body = { fontFamily: "'Manrope', sans-serif" };
const mono = { fontFamily: "'JetBrains Mono', monospace" };

export default function LandingPage() {
  const { isAuthenticated, hasCompany, loading, isCA, user } = useAuth();
  const navigate = useNavigate();
  const { t, lang } = useT();

  useEffect(() => {
    if (!loading && isAuthenticated) {
      const role = user?.role;
      if (role === "admin") navigate("/admin", { replace: true });
      else if (isCA) navigate("/ca/dashboard", { replace: true });
      else if (hasCompany) navigate("/dashboard", { replace: true });
      else navigate("/company/setup", { replace: true });
    }
  }, [isAuthenticated, hasCompany, loading, isCA, navigate, user]);

  const HOME_FAQS = [
    { q: "Is InvoiceSetu really free?", a: "Yes — our free plan lets you generate unlimited invoices and quotations forever, with branded PDF, customer master, and Email/WhatsApp share. No credit card. Paid plans add team access, e-sign, white-label, and CA collaboration." },
    { q: "Do I need a GSTIN to use InvoiceSetu?", a: "No. The app auto-detects whether you’re GST-registered and switches between Tax Invoice and Bill of Supply formats. Both are legally compliant in India." },
    { q: "How is this different from Tally / Zoho / Vyapar?", a: "InvoiceSetu is built for India — full GST/HSN, milestone billing, and CA collaboration baked in, with a clean web app that works equally well on desktop, tablet, and phone. Setup takes 2 minutes — no installer, no training." },
    { q: "Can my Chartered Accountant access my invoices?", a: "Yes. Invite your CA and they get a separate dashboard to view, export GSTR-1, file returns, and collaborate — no extra cost." },
    { q: "Is my data secure?", a: "All data is encrypted at rest and in transit, hosted on Indian servers, with daily backups. We never sell or share your data." },
    { q: "Can I migrate from Excel / Tally / Zoho?", a: "Yes. Excel import for customers, products, and historical invoices is one-click. Tally XML import is available on the Pro plan." },
  ];

  return (
    <div className="bg-white" data-testid="landing-page" style={body}>
      <SEO
        title="GST Invoice Software India"
        description="India's smart invoice software for small businesses, traders, freelancers and CAs. Create GST invoices, quotations, and bills in minutes. Free forever. No credit card."
        path={lang === "en" ? "/" : `/${lang}`}
        multilingual
        schemas={[ORGANIZATION_SCHEMA, WEBSITE_SCHEMA, SOFTWARE_SCHEMA, buildFAQSchema(HOME_FAQS)]}
      />

      {/* ═══════════════════════════════════════════
          SECTION 1 — HERO WITH DUAL CTAs
      ═══════════════════════════════════════════ */}
      <section
        className="relative overflow-hidden pt-16 pb-24 lg:pt-24 lg:pb-36 px-4 sm:px-6 lg:px-8"
        data-testid="hero-section"
      >
        {/* Subtle grid background */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='grid' width='60' height='60' patternUnits='userSpaceOnUse'%3E%3Cpath d='M 60 0 L 0 0 0 60' fill='none' stroke='%23000' stroke-width='1'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='url(%23grid)'/%3E%3C/svg%3E")`,
          }}
        />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-50 rounded-full blur-[120px] -translate-y-1/3 translate-x-1/4 opacity-60" />

        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">
            {/* Left — Copy */}
            <motion.div initial="hidden" animate="visible" variants={stagger}>
              <motion.span
                variants={fadeUp}
                className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-5 block"
                style={heading}
              >
                {t("home.hero.eyebrow")}
              </motion.span>

              <motion.h1
                variants={fadeUp}
                className="text-4xl sm:text-5xl lg:text-[3.6rem] font-black text-slate-900 leading-[1.05] tracking-tighter mb-6"
                style={heading}
              >
                {t("home.hero.h1.line1")}{" "}
                <span className="text-blue-600">{t("home.hero.h1.line2")}</span>{" "}
                {t("home.hero.h1.line3")}
              </motion.h1>

              <motion.p
                variants={fadeUp}
                className="text-lg text-slate-500 leading-relaxed max-w-lg mb-8"
              >
                {t("home.hero.subhead")}
              </motion.p>

              {/* Primary CTA — Frictionless Invoice Mode */}
              <motion.div variants={fadeUp} className="mb-3">
                <Link
                  to="/create-invoice"
                  data-testid="hero-cta-create-free"
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-bold rounded-lg px-8 py-4 text-base transition-all hover:from-emerald-600 hover:to-emerald-700 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 shadow-[0_6px_24px_rgba(16,185,129,0.4)]"
                  style={heading}
                  onClick={() => { try { window.gtag?.("event", "cta_create_invoice_free_clicked"); } catch { /* ignore */ } }}
                >
                  <FileText className="w-5 h-5" />
                  Create Invoice Free
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <p className="text-xs text-slate-500 mt-2" data-testid="hero-cta-subtitle">
                  No signup required. Create first, register only when you're ready to download or save.
                </p>
              </motion.div>

              {/* Secondary role-specific CTAs */}
              <motion.div variants={fadeUp} className="flex flex-wrap gap-4 mb-8">
                <Link
                  to="/auth"
                  data-testid="hero-cta-business"
                  className="inline-flex items-center gap-2 bg-blue-600 text-white font-semibold rounded-lg px-7 py-3.5 transition-all hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 shadow-[0_4px_16px_rgba(37,99,235,0.3)]"
                  style={heading}
                >
                  <Building2 className="w-4 h-4" />
                  {t("home.hero.ctaBusiness")}
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/auth"
                  data-testid="hero-cta-ca"
                  className="inline-flex items-center gap-2 bg-white border-2 border-slate-200 text-slate-900 font-semibold rounded-lg px-7 py-3.5 transition-all hover:border-slate-300 hover:bg-slate-50 focus:ring-2 focus:ring-slate-300 focus:ring-offset-2"
                  style={heading}
                >
                  <Briefcase className="w-4 h-4" />
                  {t("home.hero.ctaCA")}
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </motion.div>

              {/* Trust strip */}
              <motion.div variants={fadeUp} className="flex flex-wrap gap-x-6 gap-y-2">
                {[
                  { icon: <Shield className="w-3.5 h-3.5" />, text: t("home.hero.trustGst") },
                  { icon: <Lock className="w-3.5 h-3.5" />, text: t("home.hero.trustSecurity") },
                  { icon: <Users className="w-3.5 h-3.5" />, text: t("home.hero.trustWorkflow") },
                ].map((tr, i) => (
                  <span key={i} className="flex items-center gap-1.5 text-sm text-slate-400">
                    <span className="text-slate-500">{tr.icon}</span> {tr.text}
                  </span>
                ))}
              </motion.div>
            </motion.div>

            {/* Right — Invoice Mockup */}
            <motion.div
              className="relative hidden md:block"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3, duration: 0.65, ease: [0.25, 0.46, 0.45, 0.94] }}
              aria-hidden="true"
            >
              <div className="rounded-xl border border-slate-200 shadow-[0_24px_80px_rgba(0,0,0,0.08)] overflow-hidden bg-white">
                {/* Browser bar */}
                <div className="bg-slate-50 px-5 py-3 flex items-center gap-2 border-b border-slate-100">
                  <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-slate-300" />
                    <div className="w-2.5 h-2.5 rounded-full bg-slate-300" />
                    <div className="w-2.5 h-2.5 rounded-full bg-slate-300" />
                  </div>
                  <div className="flex-1 mx-4 h-6 bg-slate-100 rounded-md flex items-center px-3">
                    <span className="text-[10px] text-slate-400" style={mono}>invoicesetu.in/invoice/INV-2026-0047</span>
                  </div>
                </div>
                {/* Invoice content */}
                <div className="p-6 space-y-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em]" style={mono}>Tax Invoice</p>
                      <p className="text-sm font-bold text-slate-900 mt-0.5" style={mono}>INV/2026-27/0047</p>
                      <p className="text-[10px] text-slate-400 mt-0.5">GSTIN: 27AABCU9603R1ZP</p>
                    </div>
                    <div className="px-3 py-1 rounded-md bg-emerald-50 text-emerald-700 text-xs font-bold border border-emerald-200">
                      Paid
                    </div>
                  </div>

                  {/* Line items table */}
                  <div className="border border-slate-100 rounded-lg overflow-hidden">
                    <div className="grid grid-cols-12 text-[10px] font-bold text-slate-400 uppercase tracking-wider bg-slate-50 px-4 py-2" style={mono}>
                      <span className="col-span-5">Item</span>
                      <span className="col-span-2 text-right">HSN</span>
                      <span className="col-span-1 text-right">Qty</span>
                      <span className="col-span-2 text-right">Rate</span>
                      <span className="col-span-2 text-right">Amount</span>
                    </div>
                    {[
                      { name: "Steel TMT Bars 12mm", hsn: "7214", qty: "500 kg", rate: "58.00", amt: "29,000" },
                      { name: "Ambuja Cement Grade 53", hsn: "2523", qty: "200 bags", rate: "380.00", amt: "76,000" },
                      { name: "Birla White Putty 40kg", hsn: "3214", qty: "50 bags", rate: "820.00", amt: "41,000" },
                    ].map((r, i) => (
                      <div key={i} className="grid grid-cols-12 text-xs px-4 py-2.5 border-t border-slate-50 items-center">
                        <span className="col-span-5 font-medium text-slate-800">{r.name}</span>
                        <span className="col-span-2 text-right text-slate-400" style={mono}>{r.hsn}</span>
                        <span className="col-span-1 text-right text-slate-500">{r.qty}</span>
                        <span className="col-span-2 text-right text-slate-500" style={mono}>{r.rate}</span>
                        <span className="col-span-2 text-right font-semibold text-slate-900" style={mono}>{r.amt}</span>
                      </div>
                    ))}
                  </div>

                  {/* Totals */}
                  <div className="flex justify-between items-end">
                    <div className="space-y-1 text-[10px] text-slate-400" style={mono}>
                      <p>CGST @9%: <span className="text-slate-600">13,140</span></p>
                      <p>SGST @9%: <span className="text-slate-600">13,140</span></p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] text-slate-400 uppercase tracking-wider" style={mono}>Grand Total</p>
                      <p className="text-2xl font-black text-slate-900" style={heading}>
                        <span className="text-base font-bold text-slate-400 mr-0.5">INR</span>1,72,280
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating stat card */}
              <motion.div
                className="absolute -bottom-4 -left-6 bg-white rounded-xl border border-slate-200 shadow-lg px-4 py-3 flex items-center gap-3"
                animate={{ y: [0, -5, 0] }}
                transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
              >
                <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-[10px] text-slate-400 font-medium">Collected this month</p>
                  <p className="text-sm font-bold text-slate-900" style={mono}>INR 18,42,500</p>
                </div>
              </motion.div>

              {/* Floating GST badge */}
              <motion.div
                className="absolute -top-3 -right-3 bg-blue-600 text-white rounded-lg px-3 py-2 shadow-lg"
                animate={{ y: [0, -4, 0] }}
                transition={{ repeat: Infinity, duration: 3.5, ease: "easeInOut", delay: 0.5 }}
              >
                <p className="text-[10px] font-bold uppercase tracking-wider">GST</p>
                <p className="text-xs font-medium opacity-80">Auto-calculated</p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>


      {/* ═══════════════════════════════════════════
          SECTION 2 — PROBLEM STATEMENT (SPLIT)
      ═══════════════════════════════════════════ */}
      <Section
        className="py-24 lg:py-32 px-4 sm:px-6 lg:px-8 bg-slate-50"
        testId="problem-section"
      >
        <div className="max-w-7xl mx-auto">
          <motion.div variants={fadeUp} className="mb-14">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 block mb-3" style={heading}>The Status Quo</span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900" style={heading}>
              Two sides of the same broken system.
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            {/* Business Owner Pain */}
            <motion.div
              variants={fadeUp}
              className="border border-slate-200 bg-white rounded-xl p-8 lg:p-10"
              data-testid="problem-business"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-red-500" />
                </div>
                <h3 className="text-xl font-bold text-slate-900" style={heading}>The Business Owner</h3>
              </div>
              <div className="space-y-5">
                {[
                  { title: "Delayed payments, zero visibility", desc: "You invoice. Then you wait. Then you forget who owes you what." },
                  { title: "GST compliance anxiety", desc: "CGST, SGST, IGST, HSN codes, reverse charge — one wrong field and you face penalties." },
                  { title: "Spreadsheet chaos", desc: "Copy-paste across Excel sheets. Manual totals. Version conflicts with your accountant." },
                  { title: "No single source of truth", desc: "Your invoices live in email threads, scattered folders, and random forwards. Nothing connects." },
                ].map((item, i) => (
                  <div key={i} className="flex gap-3">
                    <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{item.title}</p>
                      <p className="text-sm text-slate-500 mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* CA Pain */}
            <motion.div
              variants={fadeUp}
              className="border border-slate-200 bg-white rounded-xl p-8 lg:p-10"
              data-testid="problem-ca"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-amber-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900" style={heading}>The Chartered Accountant</h3>
              </div>
              <div className="space-y-5">
                {[
                  { title: "Chasing clients for data", desc: "Month after month, the same email: 'Please share your invoices for filing.'" },
                  { title: "Inconsistent formats", desc: "Every client sends data differently — PDF, photo, Excel, Tally export. You reconcile manually." },
                  { title: "Filing deadline pressure", desc: "GSTR-1 is due. Half your clients haven't sent their data. You work weekends." },
                  { title: "No real-time visibility", desc: "You only see the picture when the client decides to share it. Always reactive, never proactive." },
                ].map((item, i) => (
                  <div key={i} className="flex gap-3">
                    <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{item.title}</p>
                      <p className="text-sm text-slate-500 mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </Section>


      {/* ═══════════════════════════════════════════
          SECTION 3 — SOLUTION OVERVIEW (BENTO)
      ═══════════════════════════════════════════ */}
      <Section
        className="py-24 lg:py-32 px-4 sm:px-6 lg:px-8 bg-white"
        testId="solution-section"
      >
        <div className="max-w-7xl mx-auto">
          <motion.div variants={fadeUp} className="mb-14 max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 block mb-3" style={heading}>The Platform</span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900" style={heading}>
              Everything that should have existed. Now it does.
            </h2>
            <p className="text-lg text-slate-500 mt-4 leading-relaxed">
              A structured invoicing system that connects your business operations to your compliance workflows — automatically.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-8">
            {/* Large card — Invoice Builder */}
            <motion.div
              variants={fadeUp}
              className="col-span-full md:col-span-7 bg-white border border-slate-200 rounded-xl p-8 lg:p-10 hover:shadow-xl hover:border-slate-300 transition-all duration-300"
              data-testid="solution-invoice-builder"
            >
              <div className="flex items-center gap-2 mb-4">
                <FileText className="w-5 h-5 text-blue-600" />
                <span className="text-xs font-bold uppercase tracking-[0.15em] text-blue-600" style={heading}>Invoice Builder</span>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3" style={heading}>
                GST-compliant invoices in under 60 seconds
              </h3>
              <p className="text-sm text-slate-500 leading-relaxed mb-6">
                Auto-calculated taxes, HSN code suggestions, FY-based numbering, and professional PDF generation. Built for goods and services businesses across India.
              </p>
              {/* Mini invoice snippet */}
              <div className="bg-slate-50 rounded-lg border border-slate-100 p-3 sm:p-4 overflow-hidden">
                <div className="flex items-center justify-between text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2" style={mono}>
                  <span>Recent Invoice</span>
                  <span className="text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded">Sent</span>
                </div>
                {[
                  { item: "Cotton Yarn 30s Count", hsn: "5205", amount: "1,24,000" },
                  { item: "Polyester Fiber DTY", hsn: "5402", amount: "86,500" },
                ].map((r, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5 text-xs border-t border-slate-100 first:border-0 gap-2">
                    <span className="text-slate-700 font-medium truncate min-w-0 flex-1">{r.item}</span>
                    <div className="flex gap-2 sm:gap-4 flex-shrink-0">
                      <span className="text-slate-400 hidden sm:inline" style={mono}>{r.hsn}</span>
                      <span className="text-slate-900 font-semibold" style={mono}>{r.amount}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* GST Reports card */}
            <motion.div
              variants={fadeUp}
              className="col-span-full md:col-span-5 bg-slate-900 text-white rounded-xl p-8 lg:p-10 hover:shadow-xl transition-all duration-300"
              data-testid="solution-gst-reports"
            >
              <BarChart3 className="w-6 h-6 text-blue-400 mb-4" />
              <h3 className="text-xl font-bold mb-3" style={heading}>One-Click GST Reports</h3>
              <p className="text-sm text-slate-400 leading-relaxed mb-6">
                GSTR-1, GSTR-3B, HSN summary — generated instantly from your invoices. Ready for filing or CA handoff.
              </p>
              <div className="bg-white/5 border border-white/10 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-xs">
                  <span className="text-slate-400">GSTR-3B &middot; Jan 2026</span>
                  <span className="text-white font-bold" style={mono}>INR 2,34,500</span>
                </div>
                <div className="flex gap-6 text-[11px] text-slate-500" style={mono}>
                  <span>CGST: 78,166</span>
                  <span>SGST: 78,166</span>
                  <span>IGST: 78,168</span>
                </div>
              </div>
            </motion.div>

            {/* Three smaller cards */}
            {[
              {
                icon: <IndianRupee className="w-5 h-5 text-emerald-600" />,
                title: "Payment Tracking",
                desc: "Real-time receivables dashboard. See who paid, who hasn't, and aging analysis at a glance.",
                testId: "solution-payments",
              },
              {
                icon: <Eye className="w-5 h-5 text-violet-600" />,
                title: "AI Invoice Analysis",
                desc: "Upload any invoice PDF or image. Our AI extracts line items, HSN codes, and tax breakdowns automatically.",
                testId: "solution-ai",
              },
              {
                icon: <RefreshCw className="w-5 h-5 text-amber-600" />,
                title: "Recurring Invoices",
                desc: "Set up monthly, quarterly, or custom billing cycles. Invoices generate and send on schedule.",
                testId: "solution-recurring",
              },
            ].map((card, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                className="col-span-full md:col-span-4 bg-white border border-slate-200 rounded-xl p-8 hover:shadow-xl hover:border-slate-300 transition-all duration-300"
                data-testid={card.testId}
              >
                <div className="mb-4">{card.icon}</div>
                <h3 className="text-lg font-bold text-slate-900 mb-2" style={heading}>{card.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{card.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>


      {/* ═══════════════════════════════════════════
          SECTION 4 — NETWORK & SHARING (DARK)
      ═══════════════════════════════════════════ */}
      <Section
        className="py-24 lg:py-32 px-4 sm:px-6 lg:px-8 bg-slate-900"
        testId="network-section"
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left — Copy */}
            <motion.div variants={stagger}>
              <motion.span
                variants={fadeUp}
                className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-4 block"
                style={heading}
              >
                Connected Invoicing
              </motion.span>
              <motion.h2
                variants={fadeUp}
                className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-6"
                style={heading}
              >
                Your invoice lands in their ledger.{" "}
                <span className="text-slate-500">Automatically.</span>
              </motion.h2>
              <motion.p variants={fadeUp} className="text-lg text-slate-400 leading-relaxed mb-8">
                When you send an invoice, the receiver can claim it into their payables with one click.
                No re-entry. No reconciliation gaps. Both parties see the same data.
              </motion.p>
              <motion.div variants={fadeUp} className="space-y-4">
                {[
                  { title: "Cross-User Invoice Sync", desc: "Your sales invoice becomes their purchase record. Zero duplicate data entry." },
                  { title: "Public Share Links", desc: "Share invoices via a secure link. Even non-users can view and claim them." },
                  { title: "Automatic Onboarding", desc: "Invoice receivers sign up and join the network — your invoices pull them in." },
                ].map((item, i) => (
                  <div key={i} className="flex gap-3 items-start">
                    <div className="w-6 h-6 rounded-md bg-blue-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle2 className="w-3.5 h-3.5 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">{item.title}</p>
                      <p className="text-sm text-slate-500 mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            {/* Right — Animated sync visual */}
            <motion.div variants={fadeUp} className="relative" aria-hidden="true">
              <div className="flex flex-col gap-4">
                {/* Sender card */}
                <div className="bg-white/5 border border-white/10 rounded-xl p-6 backdrop-blur-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                      <Send className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-white">Gupta Steel Traders</p>
                      <p className="text-[10px] text-slate-500" style={mono}>GSTIN: 27AADCG8765M1Z5</p>
                    </div>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3 border border-white/5">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-slate-400">INV/2026-27/0089</span>
                      <span className="text-white font-bold" style={mono}>INR 4,35,600</span>
                    </div>
                    <p className="text-[10px] text-slate-500">Steel TMT Bars 16mm &middot; 2,400 kg @ INR 62/kg + GST</p>
                  </div>
                </div>

                {/* Sync arrow */}
                <motion.div
                  className="flex items-center justify-center gap-2 py-2"
                  animate={{ opacity: [0.4, 1, 0.4] }}
                  transition={{ repeat: Infinity, duration: 2 }}
                >
                  <div className="h-px w-12 bg-gradient-to-r from-transparent to-blue-500" />
                  <Network className="w-5 h-5 text-blue-400" />
                  <div className="h-px w-12 bg-gradient-to-l from-transparent to-blue-500" />
                </motion.div>

                {/* Receiver card */}
                <div className="bg-white/5 border border-white/10 rounded-xl p-6 backdrop-blur-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                      <Receipt className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-white">Mehta Construction Pvt Ltd</p>
                      <p className="text-[10px] text-slate-500" style={mono}>GSTIN: 07AABCM1234N1ZP</p>
                    </div>
                  </div>
                  <div className="bg-emerald-500/10 rounded-lg p-3 border border-emerald-500/20">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-emerald-400 font-medium">Auto-synced to Payables</span>
                      <span className="text-white font-bold" style={mono}>INR 4,35,600</span>
                    </div>
                    <p className="text-[10px] text-slate-500">Claimed via public share link &middot; 2 min ago</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </Section>


      {/* ═══════════════════════════════════════════
          SECTION 5 — CA SECTION
      ═══════════════════════════════════════════ */}
      <Section
        className="py-24 lg:py-32 px-4 sm:px-6 lg:px-8 bg-white"
        testId="ca-section"
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left — Visual */}
            <motion.div variants={fadeUp} className="order-2 lg:order-1">
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 lg:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-violet-600" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-900" style={heading}>CA Dashboard</p>
                    <p className="text-[10px] text-slate-400">All clients, one view</p>
                  </div>
                </div>

                {/* Client list mock */}
                <div className="space-y-3">
                  {[
                    { name: "Gupta Steel Traders", invoices: 42, gst: "Filed", status: "active" },
                    { name: "Mehta Construction Pvt Ltd", invoices: 28, gst: "Pending", status: "active" },
                    { name: "Lakshmi Textiles", invoices: 35, gst: "Filed", status: "active" },
                    { name: "Kumar Auto Parts", invoices: 19, gst: "Overdue", status: "warning" },
                  ].map((client, i) => (
                    <div key={i} className="flex items-center justify-between bg-white border border-slate-100 rounded-lg px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600">
                          {client.name.charAt(0)}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-800">{client.name}</p>
                          <p className="text-[10px] text-slate-400">{client.invoices} invoices</p>
                        </div>
                      </div>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${
                        client.gst === "Filed" ? "bg-emerald-50 text-emerald-700" :
                        client.gst === "Pending" ? "bg-amber-50 text-amber-700" :
                        "bg-red-50 text-red-700"
                      }`}>
                        GST: {client.gst}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Right — Copy */}
            <motion.div variants={stagger} className="order-1 lg:order-2">
              <motion.span
                variants={fadeUp}
                className="text-xs font-bold uppercase tracking-[0.2em] text-violet-600 mb-4 block"
                style={heading}
              >
                For Chartered Accountants
              </motion.span>
              <motion.h2
                variants={fadeUp}
                className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-6"
                style={heading}
              >
                Your clients, managed on autopilot.
              </motion.h2>
              <motion.p variants={fadeUp} className="text-lg text-slate-500 leading-relaxed mb-8">
                Stop chasing clients for invoices. When they create invoices on InvoiceSetu,
                you see everything in real-time — GST data, summaries, and filing-ready reports.
              </motion.p>
              <motion.div variants={fadeUp} className="space-y-4">
                {[
                  { icon: <Layers className="w-4 h-4 text-violet-600" />, title: "Multi-Client Dashboard", desc: "Switch between client books instantly. See invoices, GST liability, and filing status." },
                  { icon: <UserCheck className="w-4 h-4 text-violet-600" />, title: "Invite & Onboard Clients", desc: "Send an invitation link. When they join, their data flows to your dashboard automatically." },
                  { icon: <Zap className="w-4 h-4 text-violet-600" />, title: "Filing-Ready Reports", desc: "GSTR-1 and GSTR-3B computed in real-time. Download and file without manual reconciliation." },
                ].map((item, i) => (
                  <div key={i} className="flex gap-3 items-start">
                    <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center flex-shrink-0">
                      {item.icon}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{item.title}</p>
                      <p className="text-sm text-slate-500 mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </motion.div>

              <motion.div variants={fadeUp} className="mt-8">
                <Link
                  to="/auth"
                  data-testid="ca-section-cta"
                  className="inline-flex items-center gap-2 bg-violet-600 text-white font-semibold rounded-lg px-6 py-3 text-sm transition-all hover:bg-violet-700 shadow-[0_4px_16px_rgba(124,58,237,0.3)]"
                  style={heading}
                >
                  Start as a CA/Professional <ArrowUpRight className="w-4 h-4" />
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </Section>


      {/* ═══════════════════════════════════════════
          SECTION 6 — CLIENT BENEFITS
      ═══════════════════════════════════════════ */}
      <Section
        className="py-24 lg:py-32 px-4 sm:px-6 lg:px-8 bg-slate-50"
        testId="benefits-section"
      >
        <div className="max-w-7xl mx-auto">
          <motion.div variants={fadeUp} className="mb-14 max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 block mb-3" style={heading}>For Business Owners</span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900" style={heading}>
              Focus on your business. We handle the paperwork.
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: <Zap className="w-6 h-6 text-blue-600" />,
                title: "Faster Payments",
                desc: "Professional invoices with UPI QR codes get paid faster. Track every rupee in real-time.",
                stat: "2x",
                statLabel: "faster collection",
              },
              {
                icon: <Shield className="w-6 h-6 text-emerald-600" />,
                title: "Error-Free GST",
                desc: "Auto-detect intra/inter-state, split CGST/SGST/IGST, and suggest correct HSN codes. No penalties.",
                stat: "0",
                statLabel: "compliance errors",
              },
              {
                icon: <BarChart3 className="w-6 h-6 text-violet-600" />,
                title: "Instant Reports",
                desc: "GSTR-1, GSTR-3B, HSN summary, receivables aging — one click. Hand off to your CA instantly.",
                stat: "1-click",
                statLabel: "report generation",
              },
              {
                icon: <Users className="w-6 h-6 text-amber-600" />,
                title: "Customer & Product Master",
                desc: "Maintain a central directory. Auto-fill customer details and product info on every invoice.",
                stat: "60s",
                statLabel: "per invoice",
              },
              {
                icon: <RefreshCw className="w-6 h-6 text-teal-600" />,
                title: "Recurring Billing",
                desc: "Set it once. Monthly rent, retainers, subscriptions — invoices generate automatically on schedule.",
                stat: "Auto",
                statLabel: "scheduling",
              },
              {
                icon: <TrendingUp className="w-6 h-6 text-rose-600" />,
                title: "Business Analytics",
                desc: "Revenue trends, top customers, product-wise breakdowns, and cash flow projections — all visual.",
                stat: "360\u00b0",
                statLabel: "visibility",
              },
            ].map((benefit, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                className="bg-white border border-slate-200 rounded-xl p-8 hover:shadow-xl hover:border-slate-300 transition-all duration-300 group"
                data-testid={`benefit-card-${i}`}
              >
                <div className="mb-5">{benefit.icon}</div>
                <h3 className="text-lg font-bold text-slate-900 mb-2" style={heading}>{benefit.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed mb-5">{benefit.desc}</p>
                <div className="pt-4 border-t border-slate-100">
                  <span className="text-2xl font-black text-slate-900" style={heading}>{benefit.stat}</span>
                  <span className="text-xs text-slate-400 ml-2 uppercase tracking-wider">{benefit.statLabel}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>


      {/* ═══════════════════════════════════════════
          SECTION 7 — TRUST & PRIVACY
      ═══════════════════════════════════════════ */}
      <Section
        className="py-24 lg:py-32 px-4 sm:px-6 lg:px-8 bg-white"
        testId="trust-section"
      >
        <div className="max-w-3xl mx-auto text-center">
          <motion.span
            variants={fadeUp}
            className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-4 block"
            style={heading}
          >
            Security & Compliance
          </motion.span>
          <motion.h2
            variants={fadeUp}
            className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-6"
            style={heading}
          >
            Your financial data is non-negotiable.
          </motion.h2>
          <motion.p variants={fadeUp} className="text-lg text-slate-500 leading-relaxed mb-14">
            We treat your invoicing data with the same seriousness as a bank treats your deposits.
            Enterprise-grade security, not startup-grade promises.
          </motion.p>

          <motion.div variants={staggerFast} className="grid sm:grid-cols-3 gap-8">
            {[
              {
                icon: <ShieldCheck className="w-8 h-8 text-blue-600" />,
                title: "End-to-End Encryption",
                desc: "HTTPS-only access, bcrypt password hashing, HttpOnly JWT cookies, and security headers (HSTS, CSP, X-Frame). Your data is protected end-to-end.",
              },
              {
                icon: <Server className="w-8 h-8 text-emerald-600" />,
                title: "India-Hosted Infrastructure",
                desc: "Your data stays in India. Compliant with data localization requirements and Indian IT regulations.",
              },
              {
                icon: <Lock className="w-8 h-8 text-slate-700" />,
                title: "Role-Based Access",
                desc: "Granular permissions. CAs see only what clients authorize. Business data is isolated per company.",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                className="text-center"
                data-testid={`trust-item-${i}`}
              >
                <div className="w-16 h-16 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center mx-auto mb-5">
                  {item.icon}
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2" style={heading}>{item.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </Section>


      {/* ═══════════════════════════════════════════
          SECTION 8 — HOW IT WORKS
      ═══════════════════════════════════════════ */}
      <Section
        className="py-24 lg:py-32 px-4 sm:px-6 lg:px-8 bg-slate-50"
        testId="how-it-works-section"
      >
        <div className="max-w-5xl mx-auto">
          <motion.div variants={fadeUp} className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 block mb-3" style={heading}>How It Works</span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900" style={heading}>
              Three steps. That's it.
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            {[
              {
                num: "01",
                title: "Create & Send",
                desc: "Add your products, select a customer, and generate a GST-compliant invoice. PDF, email, or share link — your choice.",
                icon: <FileText className="w-6 h-6 text-blue-600" />,
              },
              {
                num: "02",
                title: "Track & Sync",
                desc: "Monitor payment status in real-time. When you share an invoice, the receiver claims it into their books — no manual entry.",
                icon: <Network className="w-6 h-6 text-emerald-600" />,
              },
              {
                num: "03",
                title: "Report & File",
                desc: "One-click GST reports. Your CA sees everything on their dashboard. Filing becomes a 5-minute job, not a 5-day scramble.",
                icon: <BarChart3 className="w-6 h-6 text-violet-600" />,
              },
            ].map((step, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                className="relative"
                data-testid={`step-${step.num}`}
              >
                <div className="mb-6">
                  <span
                    className="text-6xl font-black text-slate-200 leading-none"
                    style={heading}
                  >
                    {step.num}
                  </span>
                </div>
                <div className="flex items-center gap-2 mb-3">
                  {step.icon}
                  <h3 className="text-xl font-bold text-slate-900" style={heading}>{step.title}</h3>
                </div>
                <p className="text-sm text-slate-500 leading-relaxed">{step.desc}</p>
                {i < 2 && (
                  <div className="hidden md:block absolute top-8 -right-6 lg:-right-8">
                    <ArrowRight className="w-5 h-5 text-slate-300" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </Section>


      {/* ═══════════════════════════════════════════
          SECTION 9 — FINAL CTA
      ═══════════════════════════════════════════ */}
      <Section
        className="py-24 lg:py-32 px-4 sm:px-6 lg:px-8 bg-blue-600"
        testId="final-cta-section"
      >
        <div className="max-w-3xl mx-auto text-center">
          <motion.h2
            variants={fadeUp}
            className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-white mb-6"
            style={heading}
          >
            Standardize your billing.{" "}
            <span className="text-blue-200">Starting today.</span>
          </motion.h2>
          <motion.p variants={fadeUp} className="text-lg text-blue-100 leading-relaxed mb-10 max-w-xl mx-auto">
            Join businesses and accountants across India who have replaced spreadsheets and guesswork with a structured, compliant invoicing system.
          </motion.p>

          <motion.div variants={fadeUp} className="flex flex-wrap justify-center gap-4">
            <Link
              to="/auth"
              data-testid="final-cta-business"
              className="inline-flex items-center gap-2 bg-white text-blue-600 font-bold rounded-lg px-8 py-4 text-base transition-all hover:bg-blue-50 shadow-[0_4px_20px_rgba(0,0,0,0.15)]"
              style={heading}
            >
              <Building2 className="w-5 h-5" />
              Start as Business Owner
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/auth"
              data-testid="final-cta-ca"
              className="inline-flex items-center gap-2 bg-blue-700 text-white font-bold rounded-lg px-8 py-4 text-base transition-all hover:bg-blue-800 border-2 border-blue-500"
              style={heading}
            >
              <Briefcase className="w-5 h-5" />
              Start as CA / Professional
              <ChevronRight className="w-4 h-4" />
            </Link>
          </motion.div>

          <motion.p variants={fadeUp} className="text-sm text-blue-200 mt-8">
            Free to start. No credit card required. Setup in under 2 minutes.
          </motion.p>
        </div>
      </Section>


      {/* ═══════════════════════════════════════════
          FAQ SECTION — for SEO + AI search readiness
      ═══════════════════════════════════════════ */}
      <Section className="py-20 px-4 sm:px-6 lg:px-8 border-t border-slate-100" testId="faq-section">
        <div className="max-w-3xl mx-auto">
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-black text-slate-900 text-center mb-3 tracking-tight" style={heading}>
            {t("home.faq.title")}
          </motion.h2>
          <motion.p variants={fadeUp} className="text-center text-slate-500 mb-10">
            {t("home.faq.subtitle")}
          </motion.p>
          <motion.div variants={stagger} className="space-y-2">
            {HOME_FAQS.map((f, i) => <HomeFAQItem key={i} q={f.q} a={f.a} index={i} />)}
          </motion.div>
        </div>
      </Section>

      {/* ═══════════════════════════════════════════
          POPULAR SEARCHES — Internal SEO link cluster
      ═══════════════════════════════════════════ */}
      <Section className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-50/60 border-t border-slate-100" testId="popular-searches">
        <div className="max-w-5xl mx-auto">
          <p className="text-[11px] font-bold text-slate-400 uppercase tracking-[0.18em] text-center mb-4">{t("home.popular")}</p>
          <div className="flex flex-wrap justify-center gap-2">
            {[
              { to: "/gst-invoice-generator", label: "GST Invoice Generator" },
              { to: "/free-invoice-generator", label: "Free Invoice Generator" },
              { to: "/quotation-maker", label: "Quotation Maker" },
              { to: "/tax-invoice-format", label: "Tax Invoice Format India" },
              { to: "/bill-format-small-business", label: "Bill Format for Small Business" },
              { to: "/proforma-invoice-generator", label: "Proforma Invoice Generator" },
              { to: "/freelancer-invoice-tool", label: "Freelancer Invoice Tool" },
              { to: "/invoice-software-traders", label: "Invoice Software for Traders" },
              { to: "/billing-app-shop-owners", label: "Billing App for Shop Owners" },
              { to: "/non-gst-bill-generator", label: "Non-GST Bill Generator" },
              { to: "/for-ca", label: "For Chartered Accountants" },
              { to: "/blog", label: "Learning Center" },
              { to: "/sample/invoice", label: "Sample Tax Invoice" },
              { to: "/sample/quotation", label: "Sample Quotation" },
            ].map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="text-xs sm:text-sm bg-white border border-slate-200 rounded-full px-3 py-1.5 text-slate-600 hover:text-blue-700 hover:border-blue-300 transition-colors"
              >
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      </Section>

      {/* ═══════════════════════════════════════════
          FOOTER
      ═══════════════════════════════════════════ */}
      <footer className="py-10 px-4 sm:px-6 lg:px-8 border-t border-slate-100 bg-white" data-testid="footer">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="/assets/logo.png" alt="InvoiceSetu" className="w-8 h-8 rounded-lg object-contain" />
            <span className="text-sm font-bold text-slate-900" style={heading}>
              Invoice<span className="text-blue-600">Setu</span>
            </span>
          </div>
          <p className="text-sm text-slate-400">
            {t("footer.tagline")}
          </p>
          <div className="flex flex-wrap gap-4 sm:gap-6">
            <Link to="/auth" className="text-sm text-slate-500 hover:text-slate-700 transition-colors">{t("footer.sign_in")}</Link>
            <Link to="/pricing" className="text-sm text-slate-500 hover:text-slate-700 transition-colors">{t("footer.pricing")}</Link>
            <Link to="/blog" className="text-sm text-slate-500 hover:text-slate-700 transition-colors">{t("footer.blog")}</Link>
            <Link to="/for-ca" className="text-sm text-slate-500 hover:text-slate-700 transition-colors">{t("footer.forCA")}</Link>
            <Link to="/sample/invoice" className="text-sm text-slate-500 hover:text-slate-700 transition-colors">{t("footer.sample")}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ── Homepage FAQ accordion item ── */
function HomeFAQItem({ q, a, index }) {
  const [open, setOpen] = useState(index === 0);
  return (
    <div className="bg-white rounded-xl border border-slate-100 hover:border-slate-200 transition-colors" data-testid={`home-faq-${index}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full text-left px-5 py-4 flex items-center justify-between gap-3"
        aria-expanded={open}
      >
        <span className="font-semibold text-slate-900 text-sm sm:text-base">{q}</span>
        <ChevronDown className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-5 pb-4 text-sm text-slate-600 leading-relaxed">{a}</div>}
    </div>
  );
}
